<template>
  <div class="socios-metricas mt-4">
    <!-- Header con control de expansión -->
    <div class="card shadow-sm border-0">
      <div class="card-header bg-white border-0" @click="toggleExpanded" style="cursor: pointer;">
        <div class="row align-items-center">
          <div class="col">
            <h5 class="mb-0">
              <i class="fa fa-chart-line mr-2 text-primary"></i>
              Métricas Avanzadas
            </h5>
            <small class="text-muted">
              Análisis detallado basado en los filtros de búsqueda aplicados
            </small>
          </div>
          <div class="col-auto">
            <button class="btn btn-sm btn-outline-primary rounded-circle" style="width: 36px; height: 36px;">
              <i class="fa" :class="expanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Contenido expandible -->
      <transition name="expand" @enter="enter" @leave="leave">
        <div v-if="expanded" class="card-body">
          <!-- Loading state -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary mb-3" role="status">
              <span class="sr-only">Cargando métricas...</span>
            </div>
            <p class="text-muted">Calculando estadísticas...</p>
          </div>

          <!-- Contenido de métricas -->
          <div v-else-if="metricas">
            <!-- Resumen General -->
            <div class="row mb-4">
              <div class="col-12">
                <h6 class="text-uppercase text-muted mb-3">
                  <i class="fa fa-users mr-2"></i>
                  Resumen General
                </h6>
              </div>
              <div class="col-md-3 col-6 mb-3">
                <div class="metric-card bg-primary">
                  <div class="metric-value">{{ formatNumber(metricas.general.totalSocios) }}</div>
                  <div class="metric-label">Total Socios</div>
                  <i class="metric-icon fa fa-users"></i>
                </div>
              </div>
              <div class="col-md-3 col-6 mb-3">
                <div class="metric-card bg-success">
                  <div class="metric-value">{{ metricas.general.porcentajeNacionales }}%</div>
                  <div class="metric-label">Nacionales</div>
                  <div class="metric-sublabel">{{ formatNumber(metricas.general.totalNacionales) }} socios</div>
                  <i class="metric-icon fa fa-flag"></i>
                </div>
              </div>
              <div class="col-md-3 col-6 mb-3">
                <div class="metric-card bg-info">
                  <div class="metric-value">{{ metricas.general.porcentajeExtranjeros }}%</div>
                  <div class="metric-label">Extranjeros</div>
                  <div class="metric-sublabel">{{ formatNumber(metricas.general.totalExtranjeros) }} socios</div>
                  <i class="metric-icon fa fa-globe"></i>
                </div>
              </div>
              <div class="col-md-3 col-6 mb-3">
                <div class="metric-card bg-warning">
                  <div class="metric-value">{{ metricas.planes.valorTotalPlanesFormateado }}</div>
                  <div class="metric-label">Valor Total Planes</div>
                  <div class="metric-sublabel">Promedio: {{ metricas.planes.valorPromedioPlanesFormateado }}</div>
                  <i class="metric-icon fa fa-dollar-sign"></i>
                </div>
              </div>
            </div>

            <!-- Distribuciones -->
            <div class="row">
              <!-- Estados de Cuenta -->
              <div class="col-lg-6 mb-4">
                <div class="card h-100 shadow-sm border-0">
                  <div class="card-header bg-light border-0">
                    <h6 class="mb-0">
                      <i class="fa fa-info-circle mr-2 text-primary"></i>
                      Distribución por Estado
                    </h6>
                  </div>
                  <div class="card-body">
                    <div v-for="estado in metricas.distribucion.porEstadoCuenta" :key="estado._id" class="mb-3">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <span class="font-weight-bold">{{ estado.glosa }}</span>
                        <span class="badge badge-pill" :class="getEstadoBadgeClass(estado.glosa)">
                          {{ formatNumber(estado.cantidad) }}
                        </span>
                      </div>
                      <div class="progress" style="height: 8px;">
                        <div 
                          class="progress-bar"
                          :class="getEstadoProgressClass(estado.glosa)"
                          :style="`width: ${getPercentage(estado.cantidad, metricas.general.totalSocios)}%`"
                        ></div>
                      </div>
                      <small class="text-muted">
                        {{ getPercentage(estado.cantidad, metricas.general.totalSocios).toFixed(1) }}% del total
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Perfiles de Cuenta -->
              <div class="col-lg-6 mb-4">
                <div class="card h-100 shadow-sm border-0">
                  <div class="card-header bg-light border-0">
                    <h6 class="mb-0">
                      <i class="fa fa-user-tag mr-2 text-primary"></i>
                      Distribución por Perfil
                    </h6>
                  </div>
                  <div class="card-body" style="max-height: 350px; overflow-y: auto;">
                    <div v-for="perfil in metricas.distribucion.porPerfilCuenta" :key="perfil._id" class="mb-3">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <span class="font-weight-bold">{{ perfil.glosa }}</span>
                        <span class="badge badge-pill badge-secondary">
                          {{ formatNumber(perfil.cantidad) }}
                        </span>
                      </div>
                      <div class="progress" style="height: 8px;">
                        <div 
                          class="progress-bar bg-secondary"
                          :style="`width: ${getPercentage(perfil.cantidad, metricas.general.totalSocios)}%`"
                        ></div>
                      </div>
                      <small class="text-muted">
                        {{ getPercentage(perfil.cantidad, metricas.general.totalSocios).toFixed(1) }}% del total
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Género -->
              <div class="col-lg-6 mb-4">
                <div class="card h-100 shadow-sm border-0">
                  <div class="card-header bg-light border-0">
                    <h6 class="mb-0">
                      <i class="fa fa-venus-mars mr-2 text-primary"></i>
                      Distribución por Género
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="text-center mb-3">
                      <div class="gender-chart">
                        <div 
                          v-for="genero in metricas.distribucion.porGenero" 
                          :key="genero.codigo"
                          class="gender-item mb-3"
                        >
                          <div class="icon-shape rounded-circle mx-auto mb-2" :style="`background-color: ${getGenderColor(genero.codigo)}`">
                            <i 
                              v-if="genero.codigo === 'HOMBRE'"
                              class="fa fa-mars fa-2x text-white"
                            ></i>
                            <i 
                              v-else-if="genero.codigo === 'MUJER'"
                              class="fa fa-venus fa-2x text-white"
                            ></i>
                            <i 
                              v-else
                              class="fa fa-user-circle fa-2x text-white"
                            ></i>
                          </div>
                          <h4 class="mb-0">{{ formatNumber(genero.cantidad) }}</h4>
                          <small class="text-muted">{{ genero.genero }}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Planes -->
              <div class="col-lg-6 mb-4">
                <div class="card h-100 shadow-sm border-0">
                  <div class="card-header bg-light border-0">
                    <h6 class="mb-0">
                      <i class="fa fa-credit-card mr-2 text-primary"></i>
                      Métricas de Planes
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <!-- Distribución de planes -->
                      <div class="col-md-6 mb-3">
                        <h6 class="text-muted small text-uppercase mb-3">Distribución</h6>
                        <div class="metric-item mb-2 p-2 bg-success text-white rounded">
                          <div class="d-flex justify-content-between align-items-center">
                            <div>
                              <span class="font-weight-bold">{{ metricas.planes.porcentajeConPlanPrincipal }}%</span>
                              <small class="ml-1">Principal</small>
                            </div>
                            <span>{{ metricas.planes.sociosConPlanPrincipal }}</span>
                          </div>
                        </div>
                        <div class="metric-item mb-2 p-2 bg-info text-white rounded" v-if="metricas.planes.sociosConPlanSecundario > 0">
                          <div class="d-flex justify-content-between align-items-center">
                            <div>
                              <span class="font-weight-bold">{{ metricas.planes.porcentajeConPlanSecundario }}%</span>
                              <small class="ml-1">Secundario</small>
                            </div>
                            <span>{{ metricas.planes.sociosConPlanSecundario }}</span>
                          </div>
                        </div>
                        <div class="metric-item p-2 bg-light rounded">
                          <div class="d-flex justify-content-between align-items-center">
                            <div>
                              <span class="font-weight-bold text-dark">{{ metricas.planes.porcentajeSinPlan }}%</span>
                              <small class="text-muted ml-1">Sin Plan</small>
                            </div>
                            <span class="text-muted">{{ formatNumber(metricas.planes.sociosSinPlan) }}</span>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Valores de planes -->
                      <div class="col-md-6 mb-3">
                        <h6 class="text-muted small text-uppercase mb-3">Valores</h6>
                        <div class="mb-3">
                          <div class="d-flex justify-content-between align-items-center mb-1">
                            <small class="text-muted">Valor Total:</small>
                            <span class="font-weight-bold text-primary">{{ metricas.planes.valorTotalPlanesFormateado }}</span>
                          </div>
                          <div class="d-flex justify-content-between align-items-center mb-1">
                            <small class="text-muted">Promedio:</small>
                            <span class="font-weight-bold">{{ metricas.planes.valorPromedioPlanesFormateado }}</span>
                          </div>
                        </div>
                        <hr class="my-2">
                        <div class="d-flex justify-content-between align-items-center mb-1">
                          <small class="text-muted">Mínimo:</small>
                          <span class="text-info">{{ metricas.planes.valorMinimoPlanFormateado }}</span>
                        </div>
                        <div class="d-flex justify-content-between align-items-center">
                          <small class="text-muted">Máximo:</small>
                          <span class="text-success">{{ metricas.planes.valorMaximoPlanFormateado }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Métodos de Acceso -->
              <div class="col-lg-6 mb-4">
                <div class="card h-100 shadow-sm border-0">
                  <div class="card-header bg-light border-0">
                    <h6 class="mb-0">
                      <i class="fa fa-lock mr-2 text-primary"></i>
                      Credenciales de Acceso
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <!-- Totales -->
                      <div class="col-md-6 mb-3">
                        <h6 class="text-muted small text-uppercase mb-3">Totales Registrados</h6>
                        <div class="access-item mb-3">
                          <div class="d-flex justify-content-between align-items-center mb-1">
                            <span>
                              <i class="fa fa-key text-info mr-2"></i>
                              Claves
                            </span>
                            <span class="badge badge-info badge-pill">
                              {{ metricas.accesos.credenciales.totalClaves }}
                            </span>
                          </div>
                          <div class="progress" style="height: 6px;">
                            <div class="progress-bar bg-info" :style="`width: ${metricas.accesos.credenciales.porcentajeConClaves}%`"></div>
                          </div>
                          <small class="text-muted">
                            {{ metricas.accesos.credenciales.sociosConClaves }} socios ({{ metricas.accesos.credenciales.porcentajeConClaves }}%)
                          </small>
                        </div>
                        <div class="access-item mb-3">
                          <div class="d-flex justify-content-between align-items-center mb-1">
                            <span>
                              <i class="fa fa-credit-card text-warning mr-2"></i>
                              Tarjetas
                            </span>
                            <span class="badge badge-warning badge-pill">
                              {{ metricas.accesos.credenciales.totalCredenciales }}
                            </span>
                          </div>
                          <div class="progress" style="height: 6px;">
                            <div class="progress-bar bg-warning" :style="`width: ${metricas.accesos.credenciales.porcentajeConCredenciales}%`"></div>
                          </div>
                          <small class="text-muted">
                            {{ metricas.accesos.credenciales.sociosConCredenciales }} socios ({{ metricas.accesos.credenciales.porcentajeConCredenciales }}%)
                          </small>
                        </div>
                        <div class="access-item">
                          <div class="d-flex justify-content-between align-items-center mb-1">
                            <span>
                              <i class="fa fa-fingerprint text-success mr-2"></i>
                              Biometrías
                            </span>
                            <span class="badge badge-success badge-pill">
                              {{ metricas.accesos.credenciales.totalBiometrias }}
                            </span>
                          </div>
                          <div class="progress" style="height: 6px;">
                            <div class="progress-bar bg-success" :style="`width: ${metricas.accesos.credenciales.porcentajeConBiometrias}%`"></div>
                          </div>
                          <small class="text-muted">
                            {{ metricas.accesos.credenciales.sociosConBiometrias }} socios ({{ metricas.accesos.credenciales.porcentajeConBiometrias }}%)
                          </small>
                        </div>
                      </div>
                      
                      <!-- Promedios -->
                      <div class="col-md-6 mb-3">
                        <h6 class="text-muted small text-uppercase mb-3">Promedio por Socio</h6>
                        <div class="text-center">
                          <div class="mb-3">
                            <div class="icon-shape small bg-info text-white rounded-circle mx-auto mb-2">
                              <i class="fa fa-key"></i>
                            </div>
                            <h5 class="mb-0">{{ parseFloat(metricas.accesos.credenciales.promedioClavesPorSocio).toFixed(2) }}</h5>
                            <small class="text-muted">Claves/Socio</small>
                          </div>
                          <div class="mb-3">
                            <div class="icon-shape small bg-warning text-white rounded-circle mx-auto mb-2">
                              <i class="fa fa-credit-card"></i>
                            </div>
                            <h5 class="mb-0">{{ parseFloat(metricas.accesos.credenciales.promedioCredencialesPorSocio).toFixed(2) }}</h5>
                            <small class="text-muted">Tarjetas/Socio</small>
                          </div>
                          <div>
                            <div class="icon-shape small bg-success text-white rounded-circle mx-auto mb-2">
                              <i class="fa fa-fingerprint"></i>
                            </div>
                            <h5 class="mb-0">{{ parseFloat(metricas.accesos.credenciales.promedioBiometriasPorSocio).toFixed(2) }}</h5>
                            <small class="text-muted">Biometrías/Socio</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Distribución por Edad -->
              <div class="col-lg-6 mb-4">
                <div class="card h-100 shadow-sm border-0">
                  <div class="card-header bg-light border-0">
                    <h6 class="mb-0">
                      <i class="fa fa-birthday-cake mr-2 text-primary"></i>
                      Distribución por Edad
                    </h6>
                  </div>
                  <div class="card-body" style="max-height: 350px; overflow-y: auto;">
                    <div v-for="edad in metricas.distribucion.porEdad" :key="edad._id" class="mb-3">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <span class="font-weight-bold">{{ edad._id }}</span>
                        <span class="badge badge-pill" :class="getEdadBadgeClass(edad._id)">
                          {{ formatNumber(edad.cantidad) }}
                        </span>
                      </div>
                      <div class="progress" style="height: 8px;">
                        <div 
                          class="progress-bar"
                          :class="getEdadProgressClass(edad._id)"
                          :style="`width: ${getPercentage(edad.cantidad, metricas.general.totalSocios)}%`"
                        ></div>
                      </div>
                      <div class="d-flex justify-content-between">
                        <small class="text-muted">
                          {{ getPercentage(edad.cantidad, metricas.general.totalSocios).toFixed(1) }}% del total
                        </small>
                        <small class="text-muted" v-if="edad.edadPromedio">
                          Promedio: {{ edad.edadPromedio.toFixed(1) }} años
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Estados de Acceso -->
              <div class="col-lg-6 mb-4">
                <div class="card h-100 shadow-sm border-0">
                  <div class="card-header bg-light border-0">
                    <h6 class="mb-0">
                      <i class="fa fa-shield-alt mr-2 text-primary"></i>
                      Estados de Acceso
                    </h6>
                  </div>
                  <div class="card-body">
                    <div v-for="estado in metricas.accesos.porEstado" :key="estado._id" class="mb-3">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <span class="font-weight-bold">
                          <i class="fa mr-1" :class="estado.glosa === 'AUTORIZADO' ? 'fa-check text-success' : 'fa-times text-danger'"></i>
                          {{ estado.glosa }}
                        </span>
                        <span class="badge badge-pill" :class="estado.glosa === 'AUTORIZADO' ? 'badge-success' : 'badge-danger'">
                          {{ formatNumber(estado.cantidad) }}
                        </span>
                      </div>
                      <div class="progress" style="height: 12px;">
                        <div 
                          class="progress-bar"
                          :class="estado.glosa === 'AUTORIZADO' ? 'bg-success' : 'bg-danger'"
                          :style="`width: ${getPercentage(estado.cantidad, metricas.general.totalSocios)}%`"
                        ></div>
                      </div>
                      <small class="text-muted">
                        {{ getPercentage(estado.cantidad, metricas.general.totalSocios).toFixed(1) }}% del total - {{ estado.descripcion }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Top Regiones -->
              <div class="col-12 mb-4">
                <div class="card shadow-sm border-0">
                  <div class="card-header bg-light border-0">
                    <h6 class="mb-0">
                      <i class="fa fa-map-marked-alt mr-2 text-primary"></i>
                      Distribución Geográfica - Top 5 Regiones
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div 
                        v-for="(region, index) in getTopRegiones(5)" 
                        :key="index"
                        class="col-md-2 col-sm-4 col-6 mb-3 text-center"
                      >
                        <div class="region-card p-3 rounded" :class="`bg-gradient-${getRegionColorClass(index)}`">
                          <i class="fa fa-map-marker-alt fa-2x mb-2 text-white"></i>
                          <h5 class="mb-1 text-white">{{ formatNumber(region.totalRegion) }}</h5>
                          <small class="text-white-50">{{ region._id || 'Sin Especificar' }}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Última actualización -->
            <div class="text-center text-muted mt-4">
              <small>
                <i class="fa fa-clock mr-1"></i>
                Última actualización: {{ formatDateTime(metricas.fechaConsulta) }}
              </small>
            </div>
          </div>

          <!-- Error state -->
          <div v-else-if="error" class="text-center py-5">
            <i class="fa fa-exclamation-triangle fa-3x text-warning mb-3"></i>
            <p class="text-muted">{{ error }}</p>
            <button class="btn btn-primary btn-sm" @click="fetchMetricas">
              <i class="fa fa-sync mr-1"></i>
              Reintentar
            </button>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SociosMetricas',
  props: {
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      expanded: true, // Por defecto expandido en desktop
      loading: false,
      error: null,
      metricas: null
    };
  },
  computed: {
    isMobile() {
      return window.innerWidth < 768;
    }
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.fetchMetricas();
      }
    }
  },
  methods: {
    toggleExpanded() {
      this.expanded = !this.expanded;
    },

    async fetchMetricas() {
      this.loading = true;
      this.error = null;

      try {
        const parameters = {};
        
        // Aplicar los mismos filtros que la tabla de socios
        if (this.filters.search) parameters.search = this.filters.search;
        if (this.filters.sucursal) parameters.sucursal = this.filters.sucursal;
        if (this.filters.estadoCuenta) parameters.estadoCuenta = this.filters.estadoCuenta;
        if (this.filters.estadoAcceso) parameters.estadoAcceso = this.filters.estadoAcceso;
        if (this.filters.perfilCuenta) parameters.perfilCuenta = this.filters.perfilCuenta;

        const response = await this.$CentralGymOPS_API.post('', {
          Header: {
            Operacion: "/v1/socios/estadisticas"
          },
          Auth: {
            Token: sessionStorage.getItem("jCxbraoQeD")
          },
          Parameters: parameters,
          Body: {}
        });

        if (response.data.result.code === 0) {
          this.metricas = response.data.dataOut;
        } else {
          throw new Error(response.data.result.message);
        }
      } catch (error) {
        console.error('Error al cargar métricas:', error);
        this.error = 'No se pudieron cargar las métricas. Por favor, intente nuevamente.';
      } finally {
        this.loading = false;
      }
    },

    // Métodos de formato
    formatNumber(value) {
      if (!value && value !== 0) return '0';
      return new Intl.NumberFormat('es-CL').format(value);
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('es-CL');
    },

    getPercentage(value, total) {
      if (!total) return 0;
      return (value / total) * 100;
    },

    // Métodos para estilos
    getEstadoBadgeClass(estado) {
      switch (estado) {
        case 'ACTIVA':
          return 'badge-success';
        case 'PENDIENTE':
          return 'badge-warning';
        case 'INACTIVA':
          return 'badge-secondary';
        case 'BLOQUEADA':
          return 'badge-danger';
        default:
          return 'badge-secondary';
      }
    },

    getEstadoProgressClass(estado) {
      switch (estado) {
        case 'ACTIVA':
          return 'bg-success';
        case 'PENDIENTE':
          return 'bg-warning';
        case 'INACTIVA':
          return 'bg-secondary';
        case 'BLOQUEADA':
          return 'bg-danger';
        default:
          return 'bg-secondary';
      }
    },

    getGenderIcon(codigo) {
      // Normalizar el código a mayúsculas y eliminar espacios
      const codigoNormalizado = codigo ? codigo.toUpperCase().trim() : '';
      
      if (codigoNormalizado === 'HOMBRE' || codigoNormalizado === 'MASCULINO' || codigoNormalizado === 'M') {
        return 'fa-mars';
      } else if (codigoNormalizado === 'MUJER' || codigoNormalizado === 'FEMENINO' || codigoNormalizado === 'F') {
        return 'fa-venus';
      } else {
        return 'fa-user-circle';
      }
    },

    getGenderColor(codigo) {
      // Normalizar el código a mayúsculas y eliminar espacios
      const codigoNormalizado = codigo ? codigo.toUpperCase().trim() : '';
      
      if (codigoNormalizado === 'HOMBRE' || codigoNormalizado === 'MASCULINO' || codigoNormalizado === 'M') {
        return '#3B82F6'; // Azul vibrante
      } else if (codigoNormalizado === 'MUJER' || codigoNormalizado === 'FEMENINO' || codigoNormalizado === 'F') {
        return '#EC4899'; // Rosa vibrante
      } else {
        return '#6B7280'; // Gris neutro
      }
    },

    getTopRegiones(limit = 5) {
      if (!this.metricas || !this.metricas.distribucion.geografica) return [];
      
      return this.metricas.distribucion.geografica
        .filter(r => r._id !== '')
        .sort((a, b) => b.totalRegion - a.totalRegion)
        .slice(0, limit);
    },

    getRegionColorClass(index) {
      const colors = ['primary', 'success', 'info', 'warning', 'danger'];
      return colors[index % colors.length];
    },

    getEdadBadgeClass(edad) {
      if (edad === 'Sin Fecha') return 'badge-secondary';
      if (edad === 'Menor de 18') return 'badge-info';
      if (edad === '18-24') return 'badge-primary';
      if (edad === '25-34') return 'badge-success';
      if (edad === '35-44') return 'badge-warning';
      if (edad === '45-54') return 'badge-danger';
      if (edad === '55-64') return 'badge-dark';
      if (edad === '65+') return 'badge-secondary';
      return 'badge-secondary';
    },

    getEdadProgressClass(edad) {
      if (edad === 'Sin Fecha') return 'bg-secondary';
      if (edad === 'Menor de 18') return 'bg-info';
      if (edad === '18-24') return 'bg-primary';
      if (edad === '25-34') return 'bg-success';
      if (edad === '35-44') return 'bg-warning';
      if (edad === '45-54') return 'bg-danger';
      if (edad === '55-64') return 'bg-dark';
      if (edad === '65+') return 'bg-secondary';
      return 'bg-secondary';
    },

    // Animaciones de expansión
    enter(el) {
      el.style.height = 'auto';
      const height = getComputedStyle(el).height;
      el.style.height = 0;
      getComputedStyle(el);
      setTimeout(() => {
        el.style.height = height;
      });
    },

    leave(el) {
      el.style.height = getComputedStyle(el).height;
      getComputedStyle(el);
      setTimeout(() => {
        el.style.height = 0;
      });
    }
  },

  mounted() {
    // En móvil, empezar contraído
    if (this.isMobile) {
      this.expanded = false;
    }
    
    // Cargar métricas iniciales
    this.fetchMetricas();

    // Escuchar cambios de tamaño de ventana
    window.addEventListener('resize', () => {
      if (this.isMobile && this.expanded) {
        this.expanded = false;
      }
    });
  },

  beforeDestroy() {
    window.removeEventListener('resize', () => {});
  }
};
</script>

<style scoped>
/* Animación de expansión */
.expand-enter-active,
.expand-leave-active {
  transition: height 0.3s ease-in-out;
  overflow: hidden;
}

/* Tarjetas de métricas */
.metric-card {
  position: relative;
  padding: 1.5rem;
  border-radius: 0.5rem;
  color: white;
  overflow: hidden;
  min-height: 120px;
}

.metric-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%);
  pointer-events: none;
}

.metric-value {
  font-size: 2rem;
  font-weight: bold;
  line-height: 1;
  margin-bottom: 0.5rem;
}

.metric-label {
  font-size: 0.875rem;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-sublabel {
  font-size: 0.75rem;
  opacity: 0.7;
  margin-top: 0.25rem;
}

.metric-icon {
  position: absolute;
  right: 1rem;
  bottom: 1rem;
  font-size: 3rem;
  opacity: 0.2;
}

/* Gradientes para métricas */
.bg-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.bg-gradient-success {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
}

.bg-gradient-info {
  background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
}

.bg-gradient-warning {
  background: linear-gradient(135deg, #f6ad55 0%, #ed8936 100%);
}

.bg-gradient-danger {
  background: linear-gradient(135deg, #fc8181 0%, #f56565 100%);
}

/* Tarjetas de género */
.gender-chart {
  display: flex;
  justify-content: space-around;
  align-items: center;
  flex-wrap: wrap;
}

.gender-item {
  flex: 1;
  min-width: 80px;
  text-align: center;
}

.gender-item .icon-shape {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.gender-item .icon-shape:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

/* Iconos pequeños de promedio */
.icon-shape.small {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

/* Tarjetas de región */
.region-card {
  transition: transform 0.2s ease;
}

.region-card:hover {
  transform: translateY(-2px);
}

/* Progress bars personalizadas */
.progress {
  background-color: #f0f0f0;
  border-radius: 10px;
  overflow: hidden;
}

.progress-bar {
  transition: width 0.6s ease;
  border-radius: 10px;
}

/* Responsive */
@media (max-width: 768px) {
  .metric-value {
    font-size: 1.5rem;
  }
  
  .metric-card {
    min-height: 100px;
    padding: 1rem;
  }
  
  .metric-icon {
    font-size: 2rem;
  }
  
  .gender-item {
    min-width: 60px;
  }
  
  .gender-item .icon-shape {
    width: 50px;
    height: 50px;
  }
  
  .gender-item i {
    font-size: 1.5rem !important;
  }
  
  .icon-shape.small {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
}

/* Animación para el botón de expansión */
.fa-chevron-up,
.fa-chevron-down {
  transition: transform 0.3s ease;
}

/* Efectos hover */
.card-header:hover {
  background-color: #f8f9fa !important;
}

/* Sombras y bordes */
.shadow-sm {
  box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.075) !important;
}

/* Opacidad para iconos */
.opacity-50 {
  opacity: 0.5;
}
</style>
